import React, { useEffect, useState } from "react"
import Loader from "components/Loader"
import DatePicker from "react-datepicker"
import moment from "moment"
import { useSelector } from "react-redux"
import { useQueryParam, StringParam, withDefault } from "use-query-params"
import { getBaseUrl } from "../../reducers"
import { getRegisteredSurveyorReports } from "../../../../requesters/performanceReportRequester"
import { getTeamsBlockOutList } from "../../../../requesters/userRequester"
import ReportTable from "./RegisteredSurveyorReport/ReportTable"
import { REGISTERED_SURVEYORS } from "utils/constants"

const getDate = val => {
  const momentObj = moment(val)
  return momentObj.format("YYYY-MM-DD")
}

const defaultStartDate = () => getDate(moment().startOf("month"))
const defaultEndDate = () => getDate(new Date())

const customStartDateParam = withDefault(StringParam, defaultStartDate())
const customEndDateParam = withDefault(StringParam, defaultEndDate())

const RegisteredSurveyorOverview = () => {
  const baseUrl = useSelector(getBaseUrl)
  const [requesting, setRequesting] = useState(false)
  const [summaryRows, setSummaryRows] = useState([])
  const [totalRow, setTotalRow] = useState({ jobsComplete: 0, totalWorkedTime: 0 })
  const [detailedRows, setDetailedRows] = useState([])
  const [leaveDetails, setLeaveDetails] = useState([])
  const [startDate, setStartDate] = useQueryParam("rs_start_date", customStartDateParam)
  const [endDate, setEndDate] = useQueryParam("rs_end_date", customEndDateParam)

  const filters = { start_date: startDate, end_date: endDate }

  const fetchData = () => {
    setRequesting(true)
    const startAt = moment(startDate).startOf("day").toISOString()
    const endAt = moment(endDate).endOf("day").toISOString()
    Promise.all([
      getRegisteredSurveyorReports(baseUrl, filters),
      getTeamsBlockOutList(baseUrl, {
        by_teams: [REGISTERED_SURVEYORS],
        start_at: startAt,
        end_at: endAt
      })
    ])
      .then(([reportRes, leaveRes]) => {
        if (reportRes?.status === 200 && reportRes?.data) {
          const data = reportRes.data.json ?? reportRes.data
          setSummaryRows(data.summary ?? [])
          setTotalRow(data.total ?? { jobsComplete: 0, totalWorkedTime: 0 })
          setDetailedRows(data.detail ?? [])
        }
        if (!leaveRes?.error && leaveRes?.status === 200 && leaveRes?.response) {
          const userBlockOut = leaveRes.response.userBlockOut || {}
          const list = Array.isArray(userBlockOut) ? userBlockOut : Object.values(userBlockOut)
          setLeaveDetails(
            list.map(bo => ({
              userName: bo.attributes?.userName ?? bo.attributes?.user_name,
              leaveType: bo.attributes?.leaveType ?? bo.attributes?.leave_type,
              startAt: bo.attributes?.startAt ?? bo.attributes?.start_at,
              endAt: bo.attributes?.endAt ?? bo.attributes?.end_at
            }))
          )
        } else {
          setLeaveDetails([])
        }
      })
      .catch(err => console.log(err))
      .finally(() => setRequesting(false))
  }

  const onStartDateChange = val => setStartDate(getDate(val))
  const onEndDateChange = val => setEndDate(getDate(val))

  useEffect(() => {
    fetchData()
  }, [startDate, endDate])

  const startDateObject = moment(startDate, "YYYY-MM-DD").toDate()
  const endDateObject = moment(endDate, "YYYY-MM-DD").toDate()

  return (
    <React.Fragment>
      <div className="container-inner">
        <div className="table-listing-filters d-md-flex flex-row align-items-center">
          <div className="btn-group mr-2">
            <div className="ca-custom">
              <DatePicker
                selected={startDateObject}
                selectsStart
                startDate={startDateObject}
                endDate={endDateObject}
                dateFormat="dd/MM/yyyy"
                onChange={onStartDateChange}
                placeholderText="From"
                closeOnScroll={e => e.target === document}
                className="form-control" />
            </div>
            <div className="ca-custom">
              <DatePicker
                selected={endDateObject}
                selectsEnd
                startDate={startDateObject}
                endDate={endDateObject}
                dateFormat="dd/MM/yyyy"
                onChange={onEndDateChange}
                placeholderText="To"
                closeOnScroll={e => e.target === document}
                className="form-control" />
            </div>
          </div>
        </div>
      </div>
      {requesting ? (
        <Loader />
      ) : (
        <ReportTable
          summaryRows={summaryRows}
          totalRow={totalRow}
          detailedRows={detailedRows}
          leaveDetails={leaveDetails}
        />
      )}
    </React.Fragment>
  )
}

export default RegisteredSurveyorOverview
